import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { Icon, IconButton } from '@kontentino/ui';
import { usePostsContainer } from 'components/posts/context';
import dayjs, { Dayjs } from 'dayjs';
import React, { FC, useCallback, useMemo } from 'react';
import CalendarUtils from 'app/utils/calendar';

type SwitcherUnit = 'week' | 'month';

type Props = {
  maxDate?: Dayjs | null;
  minDate?: Dayjs | null;
  unit?: SwitcherUnit;
};

function getRangeForDate(date: Dayjs, unit: SwitcherUnit) {
  if (unit === 'week') {
    return CalendarUtils.getWeekRange(date.format('YYYY-MM-DD'));
  }

  return { start: date.startOf('month'), end: date.endOf('month') };
}

const PostsLayoutTopBarMonthSwitcher: FC<Props> = ({
  maxDate,
  minDate,
  unit = 'month',
}) => {
  const postsContainer = usePostsContainer();
  const isNextDisabled = useMemo(() => {
    return dayjs(postsContainer.filterState.startDate)
      .add(1, unit)
      .isAfter(maxDate);
  }, [maxDate, postsContainer.filterState.startDate, unit]);

  const isPreviousDisabled = useMemo(() => {
    return dayjs(postsContainer.filterState.startDate)
      .subtract(1, unit)
      .isBefore(minDate);
  }, [minDate, postsContainer.filterState.startDate, unit]);

  const increase = useCallback(() => {
    const newDate = dayjs(postsContainer.filterState.startDate).add(1, unit);
    const range = getRangeForDate(newDate, unit);
    !isNextDisabled &&
      postsContainer.changeFilter(
        {
          key: 'startDate',
          value: range.start.format('YYYY-MM-DD'),
        },
        {
          key: 'endDate',
          value: range.end.format('YYYY-MM-DD'),
        },
      );
  }, [postsContainer, isNextDisabled, unit]);

  const decrease = useCallback(() => {
    const newDate = dayjs(postsContainer.filterState.startDate).subtract(
      1,
      unit,
    );
    const range = getRangeForDate(newDate, unit);
    !isPreviousDisabled &&
      postsContainer.changeFilter(
        {
          key: 'startDate',
          value: range.start.format('YYYY-MM-DD'),
        },
        {
          key: 'endDate',
          value: range.end.format('YYYY-MM-DD'),
        },
      );
  }, [postsContainer, isPreviousDisabled, unit]);

  return (
    <div className="tw-hidden tw-gap-x-2 sm:tw-flex">
      <IconButton
        onClick={decrease}
        variant="light"
        icon={<Icon icon={faChevronLeft} />}
        size={'medium'}
        disabled={isPreviousDisabled}
      />
      <IconButton
        onClick={increase}
        variant="light"
        icon={<Icon icon={faChevronRight} />}
        size={'medium'}
        disabled={isNextDisabled}
      />
    </div>
  );
};

export default PostsLayoutTopBarMonthSwitcher;
